import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { GoalStates } from '@generated/graphql';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { buildReviewGoalCardOptions } from '@ovComponents/4-module/goalCard/buildReviewGoalCardOptions';
import GoalCard from '@ovComponents/4-module/goalCard/goalCard';
import { useEmbeddedGoalWorkflow } from '@ovComponents/4-module/goalCard/useEmbeddedGoalWorkflow';
import { useInnerWorkflow } from '@ovComponents/4-module/workflowCompletion/components/useInnerWorkflow';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Button, Skeleton, Typography } from 'hephaestus/web';
import { Loop } from 'hephaestus/webIcons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { GO_TO_STEP } from '../queries';

export const WORKFLOW_COMPLETION = `
  workflowCompletion {
    id
    objectId
    context
    workflow {
      name { ${TRANSLATED_STRING_FIELDS} }
      steps {
        id
        name { ${TRANSLATED_STRING_FIELDS} }
        subSteps {
          id
          type
        }
      }
    }
    currentStep {
      id
      name { ${TRANSLATED_STRING_FIELDS} }
      subSteps {
        id
        type
      }
    }
    currentSubStep {
      id
      options
      type
      canGoBack
      skippable
      rolesCompleteableBy {
        id
      }
    }
  }
`;

const FETCH_GOALS = gql`
  query fetchGoals($input: FetchGoalsInput!) {
    fetchGoals(input: $input) {
      goals {
        id
        name
        type
        riskQuestion1
        timeHorizon
        targetAmountCents
        timeHorizonLabel { ${TRANSLATED_STRING_FIELDS} }
        goalRiskQuestionLabel { ${TRANSLATED_STRING_FIELDS} }
        financialProduct {
          name
          theme {
            name
          }
        }
        subAccounts {
          account {
            type
          }
        }
      }
      totalCount
    }
  }
`;

export const ReviewGoal = ({
  options,
  onNext,
  setActiveWorkflowCompletion,
  workflowCompletion,
  userId,
}: {
  options: any;
  onNext: any;
  setActiveWorkflowCompletion: any;
  workflowCompletion: any;
  userId?: string;
}) => {
  const { translateBackendString } = useLocalization();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [goals, setGoals] = useState<Goal[]>();
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);
  const createdGoals = workflowData.createdGoalIds ?? [];
  const currentUserId = workflowData.currentUserId || userId;
  const { workflow: embeddedGoalWorkflow, status: embeddedGoalWorkflowStatus } =
    useEmbeddedGoalWorkflow(options?.goalWorkflow);
  const formattedOptions = useMemo(
    () =>
      buildReviewGoalCardOptions(translateBackendString, {
        options,
        workflowData,
        embeddedWorkflow: embeddedGoalWorkflow,
        embeddedWorkflowStatus: embeddedGoalWorkflowStatus,
      }),
    [options, workflowData, embeddedGoalWorkflow, embeddedGoalWorkflowStatus],
  );

  const {
    data: goalsData,
    loading: goalsDataLoading,
    refetch,
  } = useQuery(FETCH_GOALS, {
    variables: {
      input: {
        filter: {
          userId: currentUserId,
          states: [GoalStates.Active],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !currentUserId,
    fetchPolicy: 'no-cache',
  });
  const innerWorkflow = useInnerWorkflow({ refetch, workflowCompletion });

  const [goToStep] = useMutation(GO_TO_STEP());
  useEffect(() => {
    setGoals(goalsData?.fetchGoals?.goals);
  }, [goalsData]);

  const goToGoalCreate = (createAnotherGoal?: boolean) => {
    if (createAnotherGoal) {
      const subSteps = workflowCompletion.workflow.steps.flatMap(
        (x: any) => x.subSteps,
      );
      const goalCreateSubStep = subSteps.find(
        (x: any) => x.type === 'CREATE_GOAL',
      );
      goToStep({
        variables: {
          input: {
            workflowCompletionId: workflowCompletion.id,
            subStepId: goalCreateSubStep.id,
          },
        },
        onCompleted: (d: any) => {
          setWorkflowData({
            ...workflowData,
            currentGoalId: undefined,
            currentGoalName: undefined,
            currentGoalType: undefined,
            currentAccountId: undefined,
            currentAccountType: undefined,
            currentAccountState: undefined,
            currentSubAccountId: undefined,
          });
          setActiveWorkflowCompletion(d.goToSubStep.workflowCompletion);
        },
      });
    }
  };

  const onEdit = async (selectedGoal: Goal) => {
    setWorkflowData({
      ...workflowData,
      currentGoalId: selectedGoal.id,
      currentGoalName: selectedGoal.name,
      currentGoalType: selectedGoal.type,
    });
    await innerWorkflow.createWorkflowCompletion({
      currentUserId,
      workflowId: options.goalWorkflow,
      workflowContext: {
        ...workflowData,
        currentGoalId: selectedGoal.id,
        currentGoalName: selectedGoal.name,
        currentGoalType: selectedGoal.type,
      },
    });
  };

  const onSubmit = async () => {
    sendAnalytic(ovAnalyticsEvents.workflowsReviewGoalContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    await onNext();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={onSubmit}
      submitButtonProps={{
        label: t('continue'),
        disabled: goalsDataLoading,
        dataTestId: 'review-goal-continue-button',
      }}
      buttonFooter={
        options.createAnotherGoal && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flex: 1,
            }}
          >
            <Button
              data-testid='review-goal-create-another-button'
              variant='contained'
              sx={{ mt: 3, mr: 1 }}
              disabled={goalsDataLoading}
              onClick={() => goToGoalCreate(options.createAnotherGoal)}
              startIcon={<Loop />}
            >
              {t('createAnotherGoal')}
            </Button>
          </Box>
        )
      }
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackendString(options?.subtitle)}
      </Typography>
      {goalsDataLoading || !currentUserId ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
        </>
      ) : (
        goals &&
        goals
          // In KYC flows we want to render all goals for an entity, if there are created goals we want to display only the created goals
          .filter(
            (g) => createdGoals.length === 0 || createdGoals.includes(g.id),
          )
          .map((g: any) => (
            <GoalCard
              key={g.id}
              goal={g}
              onEdit={onEdit}
              options={formattedOptions}
            />
          ))
      )}
      {innerWorkflow.innerWorkflowModal}
    </WorkflowCompletionForm>
  );
};

export default ReviewGoal;
